import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SEEDSPECIES_COLLECTION } from 'constants/gcConstants';
import { UPDATE_DEFAULTS_NOT_SAVED_ERROR } from 'constants/messageConstants';
import { M_CROPS_QUERY_KEY, M_SEARCH_CROPS_QUERY_KEY } from 'constants/queryKeys';
import { fetchMCollection } from 'hooks/useFetchMCollection';
import { useResolveMOrgId } from 'hooks/useResolveMOrgId';
import type { MSeedSpecies } from 'interfaces/IMasterDb';
import { MasterDbService } from 'services/MasterDbServices';
import {
  buildMBase,
  buildMUpdateBase,
  buildMWriteBody,
  readDeviceId,
  sortByDateModifiedDesc,
} from 'utils/mDocumentFactory';

import {
  buildNewVariety,
  buildVarietyExtras,
  NewVarietyValues,
  toGcVariety,
} from './addVariety';
import { composeSeedVarietySelection, StepSelectorOption } from './stepConfig';

interface CreateMVarietyArgs {
  /** The variety's id, generated once per open form and reused on retry. */
  varietyId: string;
  /** The crop document's id if one has to be created, likewise reused. */
  cropDocId: string;
  crop: StepSelectorOption;
  values: NewVarietyValues;
}

/**
 * Adds a variety to a crop in the user's own list (OP-2498). A global crop has
 * no M document yet, so one is created holding only the new variety; the global
 * crop's rate and cost still show. A crop with an M document is updated with its
 * whole extras list, refetched first so a variety added on another device is kept.
 * Returns the crop and variety as one selection.
 */
export const useCreateMVariety = () => {
  const queryClient = useQueryClient();
  const resolveMOrgId = useResolveMOrgId();

  return useMutation<StepSelectorOption, Error, CreateMVarietyArgs>({
    mutationFn: async ({ varietyId, cropDocId, crop, values }) => {
      if (!crop.globalId) throw new Error(UPDATE_DEFAULTS_NOT_SAVED_ERROR);
      const orgId = await resolveMOrgId();
      const docs = await fetchMCollection<MSeedSpecies>(
        SEEDSPECIES_COLLECTION,
        orgId
      );
      const existing = sortByDateModifiedDesc(
        docs.filter(
          (doc) => doc.globalId === crop.globalId && doc.isActive !== false
        )
      )[0];

      const newVariety = buildNewVariety(varietyId, values);
      const { extras, clearRootVarieties } = buildVarietyExtras(
        existing,
        newVariety
      );
      const deviceId = readDeviceId();

      if (existing) {
        const response = await MasterDbService.UpdateMDocument(
          SEEDSPECIES_COLLECTION,
          buildMWriteBody(buildMUpdateBase(existing, deviceId), {
            extras,
            ...(clearRootVarieties ? { varieties: [] } : {}),
          })
        );
        // 200 with no changes means the server skipped it.
        if (!response.changes?.length) {
          throw new Error(UPDATE_DEFAULTS_NOT_SAVED_ERROR);
        }
      } else {
        // Only extras: sending name, rate, cost or varieties would hide the global values.
        await MasterDbService.CreateMDocument(
          SEEDSPECIES_COLLECTION,
          buildMWriteBody(
            buildMBase({
              id: cropDocId,
              orgId,
              globalId: crop.globalId,
              deviceId,
            }),
            { extras }
          )
        );
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [M_CROPS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [M_SEARCH_CROPS_QUERY_KEY] }),
      ]);
      return composeSeedVarietySelection(crop, toGcVariety(newVariety));
    },
  });
};
